import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./CompareSlider.css";
import {
  changeBoxToStyle,
  detectImageChanges,
  getObjectFitContainRect,
} from "./utils/changeDetection";

const photoKeyOf = (x) =>
  x?.id ?? x?.photo_id ?? x?.file_url ?? x?.photo_name ?? x?.name ?? null;
const getDate = (r) => {
  const raw =
    r?.taken_at ||
    r?.created_at ||
    r?.captured_at ||
    r?.uploaded_at ||
    r?.updated_at ||
    null;
  const dt = raw ? new Date(raw) : null;
  return Number.isFinite(dt?.getTime?.()) ? dt : null;
};
const fmtDate = (d) =>
  d
    ? d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";
const fetchPhotoAnnotations = async (apiBase, file_url) => {
  try {
    const ANN_ENDPOINT = `${String(apiBase).replace(
      /\/+$/,
      ""
    )}/photo_annotation`;
    const res = await fetch(
      `${ANN_ENDPOINT}?file_url=${encodeURIComponent(file_url)}`,
      { headers: { Accept: "application/json" } }
    );
    if (!res.ok)
      throw new Error(`GET failed ${res.status} ${await res.text()}`);
    const rows = await res.json();
    return Array.isArray(rows) ? rows : [];
  } catch (err) {
    console.error("Fetch annotations failed:", err);
    return [];
  }
};

const mapRows = (rows) =>
  (rows || []).map((r) => ({
    id: `db:${r.id}`,
    dbId: r.id,
    x: Number(r.x_pct) || 0,
    y: Number(r.ypct ?? r.y_pct) || 0,
    color: r.color || "#22c55e",
    text: r.annotation_text || "",
    editing: false,
  }));

function ChangeBox({ box, label, side }) {
  return (
    <div
      className={`cmp-diff-box cmp-diff-${side} cmp-diff-${box.changeType || "modified"}`}
      style={box.style}
      title={label}
    >
      <span className="cmp-diff-label">{label}</span>
    </div>
  );
}

export default function CompareSlider({
  sortedPhotos,
  leftIdx,
  rightIdx,
  split,
  setLeftIdx,
  setRightIdx,
  setSplit,
  apiBase,
  notes,
  setNotes,
}) {
  const wrapRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [changeDetectionOn, setChangeDetectionOn] = useState(true);
  const [sensitivity, setSensitivity] = useState(25);
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState("");
  const [changes, setChanges] = useState([]);
  const [leftImageSize, setLeftImageSize] = useState(null);
  const [rightImageSize, setRightImageSize] = useState(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const leftPhoto = useMemo(
    () => sortedPhotos[leftIdx] || {},
    [sortedPhotos, leftIdx]
  );
  const rightPhoto = useMemo(
    () => sortedPhotos[rightIdx] || {},
    [sortedPhotos, rightIdx]
  );
  const leftTime = getDate(leftPhoto)?.getTime();
  const rightTime = getDate(rightPhoto)?.getTime();
  const leftIsEarlier =
    Number.isFinite(leftTime) && Number.isFinite(rightTime)
      ? leftTime < rightTime
      : null;

  const leftUrl = leftPhoto.file_url ? `${apiBase}${leftPhoto.file_url}` : null;
  const rightUrl = rightPhoto.file_url
    ? `${apiBase}${rightPhoto.file_url}`
    : null;

  useEffect(() => {
    const loadAnnotations = async () => {
      if (leftPhoto && !notes[photoKeyOf(leftPhoto)]) {
        const rows = await fetchPhotoAnnotations(apiBase, leftPhoto.file_url);
        setNotes((m) => ({ ...m, [photoKeyOf(leftPhoto)]: mapRows(rows) }));
      }
      if (rightPhoto && !notes[photoKeyOf(rightPhoto)]) {
        const rows = await fetchPhotoAnnotations(apiBase, rightPhoto.file_url);
        setNotes((m) => ({ ...m, [photoKeyOf(rightPhoto)]: mapRows(rows) }));
      }
    };
    loadAnnotations();
  }, [leftPhoto, rightPhoto, notes, setNotes, apiBase]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };

    updateSize();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateSize);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    setLeftImageSize(null);
    setRightImageSize(null);
    setChanges([]);
    setDetectError("");
  }, [leftUrl, rightUrl]);

  useEffect(() => {
    if (!changeDetectionOn || !leftUrl || !rightUrl) {
      setChanges([]);
      setDetectError("");
      setDetecting(false);
      return undefined;
    }

    let cancelled = false;
    const runDetection = async () => {
      setDetecting(true);
      setDetectError("");
      try {
        const result = await detectImageChanges(leftUrl, rightUrl, {
          sensitivity,
          leftIsEarlier,
        });
        if (!cancelled) {
          setChanges(result.changes || []);
          if (result.meta?.alignmentFailed) {
            setDetectError(
              "Could not align these views. Pick photos with more overlapping scene content."
            );
          } else if ((result.changes || []).length === 0) {
            setDetectError("");
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Change detection failed:", err);
          setChanges([]);
          setDetectError(
            err?.message?.includes("OpenCV")
              ? "The image-analysis engine could not start. Check the connection and try again."
              : "Could not analyze these images. Try a lower sensitivity or another overlapping pair."
          );
        }
      } finally {
        if (!cancelled) setDetecting(false);
      }
    };

    const timer = setTimeout(runDetection, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [changeDetectionOn, leftUrl, rightUrl, sensitivity, leftIsEarlier]);

  const leftNotes = notes[photoKeyOf(leftPhoto) ?? "noid"] || [];
  const rightNotes = notes[photoKeyOf(rightPhoto) ?? "noid"] || [];

  const leftImageRect = getObjectFitContainRect(
    containerSize.width,
    containerSize.height,
    leftImageSize?.width,
    leftImageSize?.height
  );
  const rightImageRect = getObjectFitContainRect(
    containerSize.width,
    containerSize.height,
    rightImageSize?.width,
    rightImageSize?.height
  );

  const leftChangeBoxes = changes
    .map((box) => ({
      ...box,
      style: changeBoxToStyle(box.left, leftImageRect),
    }))
    .filter((box) => box.style);

  const rightChangeBoxes = changes
    .map((box) => ({
      ...box,
      style: changeBoxToStyle(box.right, rightImageRect),
    }))
    .filter((box) => box.style);

  const onPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!wrapRef.current) return;
      const target = wrapRef.current;

      if (!isActive) {
        setIsActive(true);
        return;
      }

      setIsDragging(true);
      target.setPointerCapture(e.pointerId);

      const handleMove = (ev) => {
        const rect = target.getBoundingClientRect();
        const newSplit = Math.min(
          100,
          Math.max(0, ((ev.clientX - rect.left) / rect.width) * 100)
        );
        setSplit(newSplit);
      };

      const handleUp = (ev) => {
        setIsDragging(false);
        target.removeEventListener("pointermove", handleMove);
        target.removeEventListener("pointerup", handleUp);
        if (ev.pointerId !== undefined) {
          target.releasePointerCapture(ev.pointerId);
        }
      };

      target.addEventListener("pointermove", handleMove);
      target.addEventListener("pointerup", handleUp);
    },
    [isActive, setIsActive, setIsDragging, setSplit]
  );

  return (
    <>
      <div
        ref={wrapRef}
        className={`compare-wrap ${isActive ? "active" : ""} ${
          isDragging ? "dragging" : ""
        }`}
        onPointerDown={onPointerDown}
      >
        <img
          className="cmp-img base"
          src={rightUrl}
          alt="After"
          draggable="false"
          onLoad={(e) => {
            setRightImageSize({
              width: e.currentTarget.naturalWidth,
              height: e.currentTarget.naturalHeight,
            });
          }}
        />

        <div
          className="cmp-img top-container"
          style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }}
        >
          <img
            className="cmp-img top"
            src={leftUrl}
            alt="Before"
            draggable="false"
            onLoad={(e) => {
              setLeftImageSize({
                width: e.currentTarget.naturalWidth,
                height: e.currentTarget.naturalHeight,
              });
            }}
          />
        </div>

        <div
          className="cmp-ann-layer base"
          style={{ clipPath: `inset(0 0 0 ${split}%)` }}
        >
          {rightNotes.map((n) => (
            <span
              key={`r-${n.id}`}
              className="cmp-note"
              style={{ left: `${n.x}%`, top: `${n.y}%`, background: n.color }}
            >
              {n.text ? <span className="cmp-note-pop">{n.text}</span> : null}
            </span>
          ))}
        </div>
        <div
          className="cmp-ann-layer top"
          style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }}
        >
          {leftNotes.map((n) => (
            <span
              key={`l-${n.id}`}
              className="cmp-note"
              style={{ left: `${n.x}%`, top: `${n.y}%`, background: n.color }}
            >
              {n.text ? <span className="cmp-note-pop">{n.text}</span> : null}
            </span>
          ))}
        </div>

        {changeDetectionOn && (
          <>
            <div
              className="cmp-diff-layer base"
              style={{ clipPath: `inset(0 0 0 ${split}%)` }}
            >
              {rightChangeBoxes.map((box) => (
                <ChangeBox
                  key={`rd-${box.id}`}
                  box={box}
                  label={box.rightLabel}
                  side="right"
                />
              ))}
            </div>
            <div
              className="cmp-diff-layer top"
              style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }}
            >
              {leftChangeBoxes.map((box) => (
                <ChangeBox
                  key={`ld-${box.id}`}
                  box={box}
                  label={box.leftLabel}
                  side="left"
                />
              ))}
            </div>
          </>
        )}

        {detecting && (
          <div className="cmp-detect-status">
            <span className="cmp-detect-spinner" />
            Aligning views and detecting object changes...
          </div>
        )}

        <div className="split-line" style={{ left: `${split}%` }} />
        <button
          type="button"
          className="split-handle"
          style={{ left: `${split}%` }}
        >
          <i className="bi bi-arrow-left-right" />
        </button>
      </div>

      <div className="cmp-controls">
        <div className="cmp-detect-row">
          <button
            type="button"
            className={`cmp-detect-toggle ${changeDetectionOn ? "on" : ""}`}
            onClick={() => setChangeDetectionOn((v) => !v)}
            aria-pressed={changeDetectionOn}
          >
            <i className="bi bi-bounding-box" />
            {changeDetectionOn ? "Change Detection On" : "Change Detection Off"}
          </button>
          <div className="cmp-detect-summary" aria-live="polite">
            {changeDetectionOn && !detecting && !detectError
              ? `${changes.length} object change${changes.length === 1 ? "" : "s"} detected`
              : detectError ||
                "Aligns both views, then highlights added, removed, or changed objects"}
          </div>
        </div>

        {changeDetectionOn && (
          <div className="cmp-sensitivity-row">
            <label htmlFor="cmp-sensitivity">Sensitivity</label>
            <input
              id="cmp-sensitivity"
              type="range"
              min={15}
              max={80}
              step={1}
              value={sensitivity}
              onChange={(e) => setSensitivity(Number(e.target.value))}
            />
            <span className="cmp-sensitivity-value">{sensitivity}</span>
          </div>
        )}
      </div>

      <div className="cmp-slider-row">
        <input
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={split}
          onChange={(e) => setSplit(Number(e.target.value))}
          aria-label="Comparison slider"
        />
        <div className="cmp-slider-label">Comparison Slider</div>
      </div>

      <div className="cmp-pickers">
        <div className="picker">
          <div className="label">Left Image (Top Layer)</div>
          <select
            value={leftIdx}
            onChange={(e) => setLeftIdx(Number(e.target.value))}
          >
            {sortedPhotos.map((sp, i) => (
              <option key={`l-${i}`} value={i}>
                {fmtDate(getDate(sp)) || `#${i + 1}`}
              </option>
            ))}
          </select>
        </div>
        <div className="picker">
          <div className="label">Right Image (Base Layer)</div>
          <select
            value={rightIdx}
            onChange={(e) => setRightIdx(Number(e.target.value))}
          >
            {sortedPhotos.map((sp, i) => (
              <option key={`r-${i}`} value={i}>
                {fmtDate(getDate(sp)) || `#${i + 1}`}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
}
